import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Printer, Loader2 } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;

const GiftCardPrint = ({ card }) => (
  <div data-testid="gift-card-result" className="gift-print aspect-[1.6] w-full max-w-lg rounded-md bg-[#0F0E0C] text-[#F4EFE7] p-8 flex flex-col justify-between shadow-xl">
    <div className="flex justify-between text-[11px] tracking-[0.3em] text-[#F4EFE7]/60">
      <span>AURA LUNE · RMT</span><span>GIFT CARD</span>
    </div>
    <div>
      <p className="font-serif text-6xl">${card.amount}</p>
      <p className="text-[14px] text-[#F4EFE7]/75 mt-2">For {card.recipient_name} · from {card.sender_name}</p>
      {card.message && <p className="text-[13px] italic text-[#F4EFE7]/60 mt-2">“{card.message}”</p>}
    </div>
    <div className="flex justify-between items-end text-[12px] text-[#F4EFE7]/60">
      <span>Book: auralune.noterro.com · 226-700-8976</span>
      <span data-testid="gift-card-code" className="font-mono text-[15px] text-[#F4EFE7] tracking-wider">{card.code}</span>
    </div>
  </div>
);

export default function PaymentSuccess() {
  useSeo('Your Gift Card | Aura Lune RMT');
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ phase: 'checking' });

  useEffect(() => {
    let n = 0; let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === 'paid') { setState(r.data.gift_card ? { phase: 'paid', card: r.data.gift_card } : { phase: 'timeout' }); return; }
        if (['expired', 'failed'].includes(r.data.payment_status)) { setState({ phase: 'failed' }); return; }
      } catch { setState({ phase: 'failed' }); return; }
      if (++n >= MAX_POLLS) { setState({ phase: 'timeout' }); return; }
      setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else setState({ phase: 'failed' });
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="bg-[#F4EFE7] min-h-screen overflow-x-hidden" data-testid="payment-success-page">
      <section className="py-20 max-w-[900px] mx-auto px-6 sm:px-8">
        <Link to="/" className="no-print text-[12px] tracking-[0.3em] text-[#8B7FB8]">AURA LUNE</Link>
        {state.phase === 'checking' && (
          <p data-testid="payment-checking" className="mt-10 flex items-center gap-2 text-[15px] text-[#1E1B18]/70"><Loader2 size={16} className="animate-spin" /> Confirming your payment…</p>
        )}
        {state.phase === 'paid' && state.card && (
          <>
            <h1 className="no-print font-serif text-4xl sm:text-5xl text-[#1E1B18] mt-8 mb-3">Thank you — your gift card is ready</h1>
            <p className="no-print text-[15px] text-[#1E1B18]/65 mb-10">Print it, save it, or forward this page. Bring the code to your appointment.</p>
            <GiftCardPrint card={state.card} />
            <button data-testid="gift-print-btn" onClick={() => window.print()}
              className="no-print mt-8 inline-flex items-center gap-2 bg-[#0F0E0C] hover:bg-[#2A2622] text-[#F4EFE7] px-6 py-3 rounded-full text-[13px] font-medium transition-colors">
              <Printer size={14} /> Print gift card
            </button>
          </>
        )}
        {['failed', 'timeout'].includes(state.phase) && (
          <div data-testid="payment-failed" className="mt-10">
            <h1 className="font-serif text-4xl text-[#1E1B18] mb-3">{state.phase === 'timeout' ? 'Still processing' : 'Payment not completed'}</h1>
            <p className="text-[15px] text-[#1E1B18]/65 mb-6">
              {state.phase === 'timeout' ? 'Refresh this page in a minute to see your gift card.' : 'No charge was made. You can try again any time.'}
            </p>
            <Link to="/gift-cards" className="text-[13px] text-[#8B7FB8] underline underline-offset-4">Back to gift cards</Link>
          </div>
        )}
      </section>
    </div>
  );
}
